import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BACKEND_URL } from '../lib/api';

/**
 * /forgot-password — anonymous form. Always shows the same confirmation
 * screen regardless of whether the email is registered, to avoid leaking
 * account-existence info to attackers.
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      // Show the same success screen on rate-limit / server errors —
      // never reveal whether an account exists.
      if (err.response?.status === 429) {
        toast.error('Too many requests. Please try again in a moment.');
      } else {
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-stone-50 flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-2xl shadow-sm p-8 sm:p-10">
        {submitted ? (
          <div className="text-center" data-testid="forgot-password-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-emerald-700" />
            </div>
            <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Check your inbox</h1>
            <p className="text-stone-600 text-sm leading-relaxed mb-6">
              If an account exists for <strong className="text-[#1F3D2B]">{email}</strong>, we&apos;ve sent
              a one-hour password reset link. Check your spam folder if you don&apos;t see it.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-[#1F3D2B] hover:underline"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-3xl text-[#1F3D2B] mb-2 text-center" data-testid="forgot-password-heading">
              Forgot your password?
            </h1>
            <p className="text-stone-600 text-sm leading-relaxed mb-8 text-center">
              No worries. Enter the email you signed up with and we&apos;ll send a reset link.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[#4A5D50]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1"
                  data-testid="forgot-password-email"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 font-medium shadow-md"
                data-testid="forgot-password-submit"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-[#4A5D50] hover:text-[#1F3D2B] hover:underline">
                <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
